<template>
  <div>
    <div class="bg-top shadow-sm"></div>
    <FormWizard
      v-if="startIndex !== null"
      ref="notificationWizard"
      id="notificationWizard"
      stepSize="xs"
      color="#008238"
      :startIndex="startIndex"
      @on-change="tabChanged"
    >
      <div slot="title">
        <div class="d-flex">
          <b-col lg="2" class="wizard-nav-item">
            <b-button
              v-if="activeTabIndex != 1"
              variant="primary"
              pill
              class="py-2 w-100 w-lg-auto"
              :disabled="mfsAdmin"
              @click="saveDraft('btn')"
              aria-label="Save Draft">
              Save<span class="d-none d-lg-inline-block">&nbsp;Draft</span>
            </b-button>
          </b-col>
          <b-col
            class="mobile-nav-title"
            v-if="isExtraSmall">
            <div>{{activeTabTitle}}</div>
          </b-col>
          <b-col lg="2" offset-lg="8" class="wizard-nav-item">
            <b-button-group class="pill-group">
              <b-button
                class="py-2 mr-2"
                variant="secondary"
                @click="back"
                aria-label="Back"
                :disabled="!activeTabIndex || tabsDisabled">
                <span class="d-none d-lg-block">Back</span>
                <font-awesome-icon class="d-block d-lg-none" icon='caret-left' size="lg" />
              </b-button>
              <b-button
                class="py-2"
                variant="secondary"
                @click="next"
                aria-label="Next"
                :disabled="tabsDisabled || nextDisabled">
                <span class="d-none d-lg-block">Next</span>
                <font-awesome-icon class="d-block d-lg-none" icon='caret-right' size="lg" />
              </b-button>
            </b-button-group>
          </b-col>
        </div>
      </div>
      <template v-slot:step="props">
        <WizardStep
          :tab="props.tab"
          :transition="props.transition"
          :key="props.tab.title"
          :index="props.index"
          @click.native="stepClicked(props.index)"
          @keyup.native="e => stepKeyUp(e, props.index)"
        />
      </template>
      <TabContent
        title="Overview"
        :icon="isExtraSmall ? '' : 'icon-overview'"
        :before-change="saveDraft">
        <OverviewForm
          :notification="notification"
          :key="notification.id"
          :ref="formRefs[0]" />
      </TabContent>
      <TabContent title="Mapping" icon="icon-mapping" :before-change="saveDraft">
        <MappingForm
          :ref="formRefs[1]"
          @navigate-to-overview="navigateToOverview"
          @save-draft="saveDraft"
        />
      </TabContent>
      <TabContent
        :title="this.isExtraSmall ? 'Activities' : 'Activity Details'"
        icon="icon-details"
        :before-change="saveDraft">
        <ActivityDetailsForm
          :notificationId="notification.id"
          :initialActivityDetails="activityDetails"
          :isLUPCTown="calcIsLUPCTown"
          :key="activityDetails.id"
          :ref="formRefs[2]"
        />
      </TabContent>
      <TabContent title="Contacts" icon="icon-contacts" :before-change="saveDraft">
        <ContactsForm :ref="formRefs[3]" />
      </TabContent>
      <TabContent title="FON Final Review" icon="icon-review">
        <Summary :ref="formRefs[4]" />
      </TabContent>
      <div slot="footer"></div>
    </FormWizard>
    <SavingSpinner :show="saving"/>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { FormWizard, TabContent, WizardStep } from 'vue-form-wizard';
import 'vue-form-wizard/dist/vue-form-wizard.min.css';
import SavingSpinner from '@/components/common/SavingSpinner.vue';
import ActivityDetailsForm from '@/components/notification/ActivityDetailsForm.vue';
import ContactsForm from '@/components/notification/ContactsForm.vue';
import MappingForm from '@/components/notification/MappingForm.vue';
import OverviewForm from '@/components/notification/OverviewForm.vue';
import Summary from '@/components/notification/summary/Summary.vue';

export default {
  name: 'NotificationForm',
  components: {
    FormWizard,
    TabContent,
    WizardStep,
    ActivityDetailsForm,
    ContactsForm,
    OverviewForm,
    MappingForm,
    SavingSpinner,
    Summary,
  },
  data() {
    return {
      saving: false,
      activeTabIndex: 0,
      activeTabTitle: '',
      startIndex: null,
      formRefs: [
        'overviewForm',
        'mappingForm',
        'activityForm',
        'contactForm',
        'reviewForm',
      ],
    };
  },
  created() {
    this.setStartIndex();
  },
  computed: {
    ...mapState('Notification', {
      loading: state => state.loading,
      notification: state => state.notification,
    }),
    ...mapState(['isExtraSmall']),
    ...mapState('Activity', {
      activityDrawing: state => state.drawing,
      activityEditing: state => state.editing,
      activities: state => state.activities,
    }),
    ...mapState('ActivityDetails', {
      activityDetails: state => state.activityDetails,
    }),
    ...mapGetters('Notification', ['isLUPCTown']),
    ...mapGetters('User', ['mfsAdmin']),
    tabsDisabled() {
      return this.activityDrawing || this.activityEditing;
    },
    calcIsLUPCTown() {
      const overviewForm = this.$refs[this.formRefs[0]];
      return (overviewForm && overviewForm.isLUPCTown) || this.isLUPCTown || false;
    },
    nextDisabled() {
      return (this.activeTabIndex === 1 && !this.activities.length)
        || (this.activeTabIndex === 3 && this.$refs[this.formRefs[3]].contactsRequired)
        || (this.$refs.notificationWizard || {}).isLastStep;
    },
  },
  methods: {
    next() {
      this.$refs.notificationWizard.nextTab();
    },
    back() {
      this.saveDraft();
      this.$refs.notificationWizard.prevTab();
    },
    navigateToOverview() {
      // Navigate to Overview tab (index 0)
      this.saveDraft();
      this.$refs.notificationWizard.navigateToTab(0);
    },
    tabChanged(_prevIndex, nextIndex) {
      this.activeTabIndex = nextIndex;
      if (this.$refs.notificationWizard.tabs[this.activeTabIndex]) {
        this.activeTabTitle = this.$refs.notificationWizard.tabs[this.activeTabIndex].title;
      }
      if (this.activeTabIndex === 1 && this.$refs[this.formRefs[1]]) {
        this.$refs[this.formRefs[1]].reloadMap();
      }
      if (this.activeTabIndex === 2 && this.$refs[this.formRefs[2]]) {
        this.$refs[this.formRefs[2]].load();
      }
      if (this.activeTabIndex === 4 && this.$refs[this.formRefs[4]]) {
        this.$refs[this.formRefs[4]].reload();
      }
      this.$scrollTo(this.$refs.notificationWizard, {
        container: document.querySelector('.wizard-tab-content'),
      });
    },
    setStartIndex() {
      // User should land on the correct form step depending on what they have already filled out
      // User has filled out a contact, doesn't count when they are the contact
      const contactEntered = (!this.notification.designatedAgent
        && this.notification.designatedAgentUser)
        || (!this.notification.landowner && this.notification.landownerUser)
        || (!this.notification.operator && this.notification.operatorUser)
        || (!this.notification.licensedForester && this.notification.licensedForesterUser);

      // User has submitted ownership form
      const ownershipCompleted = this.notification.ownershipTypeId
        && this.notification.ownershipAcresClassId;

      const activityDetailsStarted = this.activities.length && this.activityDetails.id;
      const contactsStarted = contactEntered || ownershipCompleted;
      const contactsCompleted = ownershipCompleted
        && this.notification.landownerUser
        && this.notification.operatorUser;

      // User has completed all required contact information, land on summary page
      if (activityDetailsStarted && contactsCompleted) this.startIndex = 4;
      // User has started to enter contact information, and on contacts page
      else if (activityDetailsStarted && contactsStarted) this.startIndex = 3;
      // At least one activity is mapped and activity details exist, land on the activity details
      // step
      else if (activityDetailsStarted) this.startIndex = 2;
      // At least one activity was mapped alread, user lands on mapping step
      else if (this.activities.length) this.startIndex = 1;
      // This is a new draft or the user has not mapped anything yet, user should start at the
      // beginning
      else this.startIndex = 0;

      // vue-form-wizard does not handle tab change and tab checked correctly when setting start
      // index. Use $nextTick to ensure that dom depending on the start index has rendered.
      this.$nextTick(() => {
        this.tabChanged(0, this.startIndex);
        this.checkStepsUpTo(this.startIndex);
      });
    },
    checkStepsUpTo(tabIndex) {
      for (let index = 0; index <= tabIndex; index += 1) {
        this.$refs.notificationWizard.tabs[index].checked = true;
      }
    },
    async stepClicked(stepIndex) {
      if (!this.tabsDisabled && stepIndex <= this.activeTabIndex) {
        await this.saveDraft();
        this.$refs.notificationWizard.navigateToTab(stepIndex);
      } else if (await this.saveDraft()) this.$refs.notificationWizard.navigateToTab(stepIndex);
    },
    async stepKeyUp(event, stepIndex) {
      if (event.keyCode === 13) await this.stepClicked(stepIndex);
    },
    async saveDraft(source) {
      this.saving = true;
      // Do not allow admins to save draft
      if (!this.mfsAdmin) {
        const formRef = this.formRefs[this.activeTabIndex];
        if (formRef) {
          if (source === 'btn' && formRef === 'overviewForm') {
            const formResult = await this.$refs[formRef].submitWrapper(source);
            this.saving = false;
            return formResult && !this.tabsDisabled;
          } else {
            const formResult = await this.$refs[formRef].submitWrapper();
            this.saving = false;
            return formResult && !this.tabsDisabled;
          }
        }
      }
      this.saving = false;
      return true;
    },
  },
};
</script>
